import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const API_BASE =
  (import.meta.env.VITE_PUBLIC_API_URL as string | undefined) ?? 'https://api.dgagentlaunch.com';

type Slot = { startAt: string; endAt: string };

type BookingPayload = {
  tenant: { name: string; slug: string; branding?: { primary?: string; secondary?: string } } | null;
  host: { firstName: string; lastName: string } | null;
  durationMinutes: number;
  timezone: string;
  slots: Slot[];
};

export default function PublicBooking() {
  const { slug } = useParams<{ slug: string }>();
  const [data, setData] = useState<BookingPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [picked, setPicked] = useState<Slot | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!slug) return;
    axios
      .get(`${API_BASE}/api/calendar-config/booking/${slug}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.error?.message || 'Booking page not found'));
  }, [slug]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!picked) return;
    setSubmitting(true);
    setError(null);
    try {
      await axios.post(`${API_BASE}/api/appointments/book-public`, {
        slug,
        startAt: picked.startAt,
        contactName: name,
        contactEmail: email,
        contactPhone: phone || undefined,
        notes: notes || undefined,
      });
      setSuccess(true);
    } catch (e: unknown) {
      const msg = axios.isAxiosError(e) ? e.response?.data?.error?.message : 'Booking failed';
      setError(msg || 'Booking failed');
    } finally {
      setSubmitting(false);
    }
  }

  if (error && !data) {
    return (
      <main className="min-h-screen bg-dgal-ink text-white flex items-center justify-center">
        <div>{error}</div>
      </main>
    );
  }
  if (!data) return <main className="min-h-screen bg-dgal-ink text-white flex items-center justify-center">Loading…</main>;

  if (success) {
    return (
      <main className="min-h-screen bg-dgal-ink text-white flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <div className="text-dgrp-red text-sm uppercase tracking-[0.2em] mb-4">{data.tenant?.name}</div>
          <h1 className="text-3xl font-bold mb-2">Booked!</h1>
          <p className="text-dgal-mist">
            We sent a confirmation to {email}. The team will be in touch shortly.
          </p>
        </div>
      </main>
    );
  }

  // Group slots by date
  const byDay = new Map<string, Slot[]>();
  for (const s of data.slots) {
    const day = new Date(s.startAt).toDateString();
    if (!byDay.has(day)) byDay.set(day, []);
    byDay.get(day)!.push(s);
  }

  return (
    <main className="min-h-screen bg-dgal-ink text-white">
      <header className="border-b border-dgal-navy-2 px-8 py-6 text-center">
        <div className="text-dgrp-red text-sm uppercase tracking-[0.2em] mb-1">{data.tenant?.name}</div>
        <div className="text-dgal-mist text-sm">
          Schedule with {data.host?.firstName} {data.host?.lastName} ·{' '}
          {data.durationMinutes} min · {data.timezone}
        </div>
      </header>

      <section className="max-w-4xl mx-auto px-6 py-10 grid md:grid-cols-2 gap-8">
        <div>
          <h2 className="text-sm uppercase tracking-wider text-dgal-mist mb-4">Pick a time</h2>
          {Array.from(byDay.entries()).slice(0, 14).map(([day, slots]) => (
            <div key={day} className="mb-4">
              <div className="text-xs uppercase tracking-wider text-dg-text-2 mb-2">{day}</div>
              <div className="flex flex-wrap gap-2">
                {slots.map((s) => (
                  <button
                    key={s.startAt}
                    onClick={() => setPicked(s)}
                    className={`px-3 py-1 text-sm rounded border ${
                      picked?.startAt === s.startAt
                        ? 'bg-dgrp-red text-white border-dgrp-red'
                        : 'border-dgal-navy-2 hover:border-dgrp-red/60'
                    }`}
                  >
                    {new Date(s.startAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </button>
                ))}
              </div>
            </div>
          ))}
          {data.slots.length === 0 && <div className="text-dgal-mist">No availability in the next two weeks.</div>}
        </div>

        <form onSubmit={submit} className="bg-dgal-navy/40 border border-dgrp-red/20 rounded-lg p-5 grid gap-3">
          <h2 className="text-sm uppercase tracking-wider text-dgal-mist">Your info</h2>
          {picked && (
            <div className="text-xs text-dg-text-2">
              Picked: {new Date(picked.startAt).toLocaleString()}
            </div>
          )}
          <input
            required value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name"
            className="bg-dgal-ink border border-dgal-navy-2 rounded px-3 py-2"
          />
          <input
            required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email"
            className="bg-dgal-ink border border-dgal-navy-2 rounded px-3 py-2"
          />
          <input
            value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone (optional)"
            className="bg-dgal-ink border border-dgal-navy-2 rounded px-3 py-2"
          />
          <textarea
            rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything we should know?"
            className="bg-dgal-ink border border-dgal-navy-2 rounded px-3 py-2"
          />
          {error && <div className="text-red-400 text-sm">{error}</div>}
          <button
            type="submit"
            disabled={!picked || submitting}
            className="px-4 py-2 rounded bg-dgrp-red text-white font-semibold hover:bg-dgrp-red-hover disabled:opacity-40"
          >
            {submitting ? 'Booking…' : 'Confirm booking'}
          </button>
        </form>
      </section>
    </main>
  );
}
